import { useEffect, useRef, useState } from "react";
import {
  TIME_PRESETS,
  calcHours,
  formatHrs,
  timeOptions,
  projectDisplayName,
} from "./worklogHelpers";
import "./taskPlanner.css";

export const ClockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3.5 2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
  </svg>
);

export const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

export const Highlighted = ({ text = "", term = "" }) => {
  if (!term || !String(text).toLowerCase().includes(term.toLowerCase())) return <>{text}</>;
  const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
  const parts = String(text).split(regex);
  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <span key={i} className="tp-highlight">
            {part}
          </span>
        ) : (
          part
        )
      )}
    </>
  );
};

export const ProjectSearchInput = ({ projects = [], value, onChange, error, disabled, onToggle }) => {
  const selectedProject = projects.find((p) => String(p.id || p.project_id) === String(value));
  const [search, setSearch] = useState(selectedProject ? projectDisplayName(selectedProject) : "");
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (onToggle) onToggle(open);
  }, [open, onToggle]);

  useEffect(() => {
    setSearch(selectedProject ? projectDisplayName(selectedProject) : "");
  }, [value, selectedProject]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        if (!value) setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [value]);

  const filtered = projects.filter(
    (p) =>
      projectDisplayName(p).toLowerCase().includes(search.toLowerCase()) ||
      String(p.project_code || p.code || "").toLowerCase().includes(search.toLowerCase())
  );
  const sortedFiltered = [...filtered].sort((a, b) => {
    const aName = projectDisplayName(a).toLowerCase();
    const bName = projectDisplayName(b).toLowerCase();
    const term = search.toLowerCase();
    const aM = aName.includes(term);
    const bM = bName.includes(term);
    if (aM && !bM) return -1;
    if (!aM && bM) return 1;
    return aName.localeCompare(bName);
  });

  const handleSelect = (project) => {
    const pid = project.id || project.project_id;
    setSearch(projectDisplayName(project));
    setOpen(false);
    onChange(pid, project);
  };

  return (
    <div className={`tp-search${open ? " is-open" : ""}`} ref={containerRef}>
      <input
        type="text"
        value={search}
        placeholder="Search project…"
        disabled={disabled}
        autoComplete="off"
        onFocus={() => {
          if (!value && !disabled) setOpen(true);
        }}
        onBlur={() =>
          setTimeout(() => {
            if (!value) setSearch("");
            setOpen(false);
          }, 180)
        }
        onChange={(e) => {
          setSearch(e.target.value);
          if (value) onChange("", null);
          setOpen(true);
        }}
        className={error ? "error" : ""}
      />
      {value && !disabled && (
        <button
          type="button"
          className="tp-search-clear"
          onMouseDown={(e) => {
            e.stopPropagation();
            setSearch("");
            setOpen(false);
            onChange("", null);
          }}
        >
          ×
        </button>
      )}
      {open && !disabled && (
        <div className="tp-dropdown" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}>
          {search && sortedFiltered.length > 0 && (
            <div className="tp-dropdown-hint">
              {sortedFiltered.length} of {projects.length} projects
            </div>
          )}
          {sortedFiltered.length === 0 ? (
            <div className="tp-dropdown-item">No matching projects</div>
          ) : (
            sortedFiltered.map((p) => {
              const pid = p.id || p.project_id;
              return (
                <div
                  key={pid}
                  className={`tp-dropdown-item ${pid === value ? "active" : ""}`}
                  onMouseDown={() => handleSelect(p)}
                >
                  <div>
                    <Highlighted text={projectDisplayName(p)} term={search} />
                  </div>
                  {(p.project_code || p.code) && (
                    <div className="tp-dropdown-code">
                      <Highlighted text={p.project_code || p.code} term={search} />
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export const Field = ({ label, required, children, className = "" }) => (
  <div className={`tp-field ${className}`}>
    {label ? (
      <span className="tp-field-label">
        {label}
        {required ? " *" : ""}
      </span>
    ) : null}
    {children}
  </div>
);

export const Sel = ({
  value,
  onChange,
  disabled,
  placeholder,
  children,
  error,
  loading,
  hidePlaceholder,
}) => (
  <div className="tp-select-wrap">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled || loading}
      className={`tp-select ${error ? "error" : ""}`}
    >
      {!hidePlaceholder && <option value="">{loading ? "Loading…" : placeholder}</option>}
      {children}
    </select>
    <span className="tp-select-caret">{loading ? "…" : "▾"}</span>
  </div>
);

export const TimeSel = ({ value, onChange, placeholder, error, disabled }) => (
  <div className="tp-select-wrap">
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className={`tp-select ${error ? "error" : ""}`}
    >
      <option value="">{placeholder}</option>
      {timeOptions.map((t) => (
        <option key={t} value={t}>
          {t}
        </option>
      ))}
    </select>
    <span className="tp-select-caret">▾</span>
  </div>
);

export const PresetPills = ({ onApply, disabled }) => (
  <div className={`tp-presets ${disabled ? "tp-disabled" : ""}`}>
    {TIME_PRESETS.map((p) => (
      <button key={p.label} type="button" className="tp-preset" onClick={() => onApply(p.start, p.end)}>
        {p.label}
      </button>
    ))}
  </div>
);

export const DurationBadge = ({ start, end }) => {
  const hrs = calcHours(start, end);
  const timeInvalid = start && end && end <= start;
  if (timeInvalid) return <span className="tp-duration invalid">Invalid</span>;
  if (!hrs) return <span className="tp-muted">—</span>;
  return <span className="tp-duration">{formatHrs(hrs)}</span>;
};
